// Cintoia Events (docs/features/cintoia-events.md): Cintoia's own webinars and podcast episodes,
// the same for every venue. Items live in RTDB /venuemanager/prod/events/{id}, written by the
// superadmin app; times are epoch seconds like the venue manager news.

const HOUR = 60 * 60
const DAY = 24 * HOUR

export const nowSeconds = () => Math.floor(Date.now() / 1000)

// the published items as an array with their ids
export const publishedEvents = (node) =>
  Object.keys(node || {})
    .map((id) => ({ ...node[id], id }))
    .filter((e) => e && e.published === true && e.title)

const endOf = (e) => (Number(e.start) || 0) + (Number(e.duration) || 60) * 60

export const webinarStatus = (e, now = nowSeconds()) => {
  const start = Number(e.start) || 0
  if (now >= start && now < endOf(e)) return 'live'
  if (start > now && start - now <= HOUR) return 'soon'
  return null
}

// Tulossa: webinars not yet over, soonest first. Podcastit: newest first. Menneet: latest first.
export const splitEvents = (events, now = nowSeconds()) => {
  const webinars = (events || []).filter((e) => e.type === 'webinar')
  const upcoming = webinars.filter((e) => endOf(e) > now).sort((a, b) => a.start - b.start)
  const past = webinars.filter((e) => endOf(e) <= now).sort((a, b) => b.start - a.start)
  const podcasts = (events || []).filter((e) => e.type === 'podcast').sort((a, b) => (b.start || 0) - (a.start || 0))
  return { upcoming, podcasts, past }
}

// The player URL for a YouTube, Vimeo or Spotify link, or null when it can't be embedded.
export const embedFor = (url) => {
  if (!url || typeof url !== 'string') return null
  let u
  try {
    u = new URL(url.trim())
  } catch (e) {
    return null
  }
  const host = u.hostname.replace(/^www\.|^m\./, '')
  const parts = u.pathname.split('/').filter(Boolean)
  const id = /^[\w-]+$/
  if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    const v = u.searchParams.get('v')
    if (parts[0] === 'watch' && v && id.test(v)) return 'https://www.youtube-nocookie.com/embed/' + v
    if (['live', 'shorts', 'embed'].includes(parts[0]) && parts[1] && id.test(parts[1])) return 'https://www.youtube-nocookie.com/embed/' + parts[1]
    return null
  }
  if (host === 'youtu.be') return parts[0] && id.test(parts[0]) ? 'https://www.youtube-nocookie.com/embed/' + parts[0] : null
  if (host === 'vimeo.com') return parts[0] && /^\d+$/.test(parts[0]) ? 'https://player.vimeo.com/video/' + parts[0] : null
  if (host === 'player.vimeo.com') return parts[0] === 'video' && /^\d+$/.test(parts[1] || '') ? 'https://player.vimeo.com/video/' + parts[1] : null
  if (host === 'open.spotify.com') {
    // /episode/ID, /show/ID, or the same under /embed/ or a locale prefix (/intl-fi/episode/ID)
    const at = parts.findIndex((p) => p === 'episode' || p === 'show')
    if (at >= 0 && parts[at + 1] && id.test(parts[at + 1])) return 'https://open.spotify.com/embed/' + parts[at] + '/' + parts[at + 1]
    return null
  }
  return null
}

export const isSpotify = (embed) => !!embed && embed.startsWith('https://open.spotify.com/')

// Items published after the user last opened the page. Someone who never has counts only the
// last 30 days, so a new user isn't greeted with the whole archive.
export const unseenCount = (events, seen, now = nowSeconds()) => {
  const since = Number(seen) || now - 30 * DAY
  return (events || []).filter((e) => (Number(e.created) || Number(e.start) || 0) > since).length
}

// only http(s) links are rendered, so a stored javascript: or data: URL can never run
export const safeLink = (url) => {
  if (!url || typeof url !== 'string') return null
  try {
    const u = new URL(url.trim())
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.href : null
  } catch (e) {
    return null
  }
}
